import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { useTranslation } from 'react-i18next';
import {
  Archive, Banknote, BellRing, BookOpen, Bot, Building2, ChartColumn, ChartLine, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, Clock, CodeXml, Database, Flag, Gauge, Headphones, Layers, LayoutDashboard, ListChecks, LogOut, MapPinned, MessagesSquare, Package, Phone, PhoneCall, Plug, Settings, ShoppingCart, Smartphone, Store, Tag, Ticket, Truck, UserCog, UserRound, Users, Warehouse, Workflow, Zap,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import BrandMark from './BrandMark';
import axios from 'axios';
import { ONBOARDING_EVENT } from './onboarding/events';

const getStoredUserData = () => {
  const storedUserData = localStorage.getItem('userData');

  if (!storedUserData) {
    return null;
  }

  try {
    return JSON.parse(storedUserData);
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    return null;
  }
};

// Collapsed groups by default except the daily-work ones; the user's own
// open/closed choices persist across sessions.
const SECTIONS_STORAGE_KEY = 'una_sidebar_sections';
const DEFAULT_SECTIONS = {
  tickets: true,
  orders: true,
  pilotage: true,
  clients: false,
  carriers: false,
  agents: false,
  voice: false,
  channels: false,
  data: false,
  catalogue: false,
  settings: false,
};

// Icon rail (reference design): the person's choice persists; pages offset
// themselves with var(--u-sidebar-current), switched by a class on <html>.
const COLLAPSED_STORAGE_KEY = 'una_sidebar_collapsed';
const getStoredCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch (error) {
    return false;
  }
};

// matchMedia is missing in jsdom (tests) and very old browsers: no forced rail there.
const narrowQuery = () => (typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 1024px)') : null);

const getStoredSections = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY));
    return stored && typeof stored === 'object' ? { ...DEFAULT_SECTIONS, ...stored } : DEFAULT_SECTIONS;
  } catch (error) {
    return DEFAULT_SECTIONS;
  }
};

const Sidebar = ({ userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(getStoredUserData);
  const [expandedSections, setExpandedSections] = useState(getStoredSections);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [collapsedChoice, setCollapsed] = useState(getStoredCollapsed);
  // Below 1024px the rail is forced; the person's choice applies above it.
  const [narrow, setNarrow] = useState(() => !!narrowQuery()?.matches);
  useEffect(() => {
    const query = narrowQuery();
    if (!query) return undefined;
    const onChange = (event) => setNarrow(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const collapsed = collapsedChoice || narrow;

  useEffect(() => {
    document.documentElement.classList.toggle('u-sidebar-collapsed', collapsed);
  }, [collapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, collapsedChoice ? '1' : '0');
    } catch (error) {
      // Storage blocked: the rail still toggles for this session.
    }
  }, [collapsedChoice]);
  // Catalogue produits: shown only to allow-listed tenants (the API answers
  // 404 for everyone else, so the section simply does not exist for them).
  const [catalogueEnabled, setCatalogueEnabled] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    setUserData(getStoredUserData());
  }, []);

  useEffect(() => {
    if (!['admin', 'employee'].includes(userRole)) return undefined;
    let cancelled = false;
    axios.get('/api/catalog/access', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((response) => { if (!cancelled) setCatalogueEnabled(!!response.data?.enabled); })
      .catch(() => { if (!cancelled) setCatalogueEnabled(false); });
    return () => { cancelled = true; };
  }, [userRole]);

  // « Mise en route » pinned on top while the account's onboarding is open
  // (signup, or started from Paramètres); refreshed after each step.
  const [onboarding, setOnboarding] = useState(null);
  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    let cancelled = false;
    const load = () => axios.get('/api/setup/onboarding', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setOnboarding(data.active ? { done: data.done || 0, total: data.total || 0 } : null);
      })
      .catch(() => { if (!cancelled) setOnboarding(null); });
    load();
    window.addEventListener(ONBOARDING_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(ONBOARDING_EVENT, load); };
  }, [userRole]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userData');
    navigate('/');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const next = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        // Storage full/blocked: the toggle still works for this session.
      }
      return next;
    });
  };

  const location = useLocation();

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountMenuOpen]);

  const has = (roles) => roles.includes(userRole);
  // A team member works the tickets: no settings, their own numbers only
  // (Kevin, 29/10) — « Ma productivité » and « Mon compte ».
  const isEmployee = userRole === 'employee';

  // Nav labels fit the 240 px sidebar at the active weight in French and in
  // English (polishUi.nav.*: short names; the pages keep their full titles).
  // Sidebar v3 (2026-10-07). Groups follow the day of the person using it:
  // the work itself (tickets, then orders), how it's going, then the business
  // the AI works for (clients, carriers, the channels customers use), the AI,
  // the plumbing, the settings. Per-item role gates are UNCHANGED; items only
  // moved between groups.
  const sections = [
    {
      key: 'tickets',
      label: t('sidebarNav.tickets', 'Tickets'),
      icon: <Ticket />,
      items: [
        has(['admin', 'employee', 'e-commerce', 'eshop', 'seller', 'experience'])
          && { to: '/dashboard', icon: <LayoutDashboard />, label: t('dashboard') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'experience'])
          && { to: '/tickets/archive', icon: <Archive />, label: t('archive') },
        // How the ticket list is cut into views: it belongs with the tickets.
        has(['admin', 'e-commerce'])
          && { to: '/ticket-views-parameters', icon: <Layers />, label: t('ticketViewsParameters.title', 'Vues de tickets') },
      ],
    },
    // The orders behind the tickets and the money of their claims.
    {
      key: 'orders',
      label: t('sidebarNav.orders', 'Commandes'),
      icon: <ShoppingCart />,
      items: [
        has(['admin', 'employee', 'logistics', 'logistician'])
          && { to: '/commerce/orders', icon: <ShoppingCart />, label: t('commerceOrders.title', 'Commandes') },
        has(['admin', 'employee', 'logistics', 'logistician'])
          && { to: '/refunds', icon: <Banknote />, label: t('refunds.title', 'Remboursements') },
      ],
    },
    {
      key: 'pilotage',
      label: t('sidebarNav.pilotage', 'Pilotage'),
      icon: <Gauge />,
      items: [
        has(['admin', 'e-commerce'])
          && { to: '/statistics', icon: <ChartColumn />, label: t('statistics') },
        isEmployee
          && { to: '/create-agent', icon: <ChartLine />, label: t('accountScopeUi.nav.myProductivity', 'Ma productivité') },
        has(['admin', 'e-commerce', 'seller', 'logistics', 'logistician', 'eshop'])
          && { to: '/create-agent', icon: <UserCog />, label: t('employeesPage.navLabel', 'Employés') },
      ],
    },
    // The two kinds of clients of a logistician (Kevin, 2026-10-07): B2B — the
    // merchants it ships for (clients_edn: addresses, instructions, API) — and
    // B2C — the end customers who write in (the CRM).
    {
      key: 'clients',
      label: t('sidebarNav.clients', 'Clients'),
      icon: <Users />,
      items: [
        userRole === 'admin' && !userData?.fa_account
          && { to: '/clients-instructions', icon: <Building2 />, label: t('sidebarNav.clientsB2B', 'Clients B2B') },
        has(['admin', 'employee'])
          && { to: '/clients', icon: <UserRound />, label: t('sidebarNav.clientsB2C', 'Clients B2C') },
      ],
    },
    // Everything about carriers and parcels in one place: the carriers, the
    // proactive parcel watch, the carrier follow-ups and the public tracking page.
    {
      key: 'carriers',
      label: t('sidebarNav.carriers', 'Transporteurs'),
      icon: <Truck />,
      items: [
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/carriers-instructions', icon: <Truck />, label: t('sidebarNav.carrierList', 'Transporteurs') },
        has(['admin', 'logistics'])
          && { to: '/parcel-monitor', icon: <Package />, label: t('parcelMonitor.navLabel', 'Surveillance colis') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/followup-settings', icon: <Clock />, label: t('sidebarNav.followups', 'Relances') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/tracking-settings', icon: <MapPinned />, label: t('sidebarNav.trackingPage', 'Page de suivi') },
      ],
    },
    // The surfaces the END CUSTOMER touches — chatbot, ticket form, the
    // e-mails and SMS sent to them — are channels to shape, not settings to
    // bury (Kevin, 2026-10-06 / 2026-10-07).
    has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience']) && {
      key: 'channels',
      label: t('sidebarNav.channels', 'Canaux clients'),
      icon: <MessagesSquare />,
      items: [
        { to: '/chatbot-settings', icon: <MessagesSquare />, label: t('polishUi.nav.chatbot', 'Chatbot') },
        { to: '/ticket-form-settings', icon: <Ticket />, label: t('ticketFormSettings.navLabel', 'Formulaire de tickets') },
        { to: '/notifications-settings', icon: <BellRing />, label: t('polishUi.nav.emailNotifications', 'Notifications e-mail') },
        has(['admin', 'logistics'])
          && { to: '/sms-notifications', icon: <Smartphone />, label: t('sidebarNav.smsNotifications', 'Notifications SMS') },
      ],
    },
    {
      key: 'agents',
      label: t('sidebarNav.agentsSection', 'Agents IA'),
      icon: <Bot />,
      items: [
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/my-ai-assistants', icon: <Bot />, label: t('aiAgentsLabs') },
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/knowledge-board', icon: <BookOpen />, label: t('aiAssistant.knowledgeBoard') },
        has(['admin', 'e-commerce'])
          && { to: '/ai-automation', icon: <Zap />, label: t('sidebarNav.automation', 'Automatisation') },
        has(['admin', 'e-commerce'])
          && { to: '/router-agent', icon: <Workflow />, label: t('routerAgent.title', 'Agent Routeur') },
        userRole === 'admin'
          && { to: '/warehouses-instructions', icon: <Warehouse />, label: t('polishUi.nav.warehouses', 'Entrepôts') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/status-instructions', icon: <ListChecks />, label: t('statusInstructions.title', 'Instructions de statut') },
      ],
    },
    // Voice agents answer the phone: their own product (agents, numbers,
    // calls), not a tab of a text agent nor a page of the account settings
    // (Kevin, 2026-10-07).
    has(['admin']) && {
      key: 'voice',
      label: t('sidebarNav.voice', 'Agents vocaux'),
      icon: <Headphones />,
      items: [
        { to: '/voice-agents', icon: <Headphones />, label: t('sidebarNav.voiceAgents', 'Agents') },
        { to: '/voice-numbers', icon: <Phone />, label: t('sidebarNav.voiceNumbers', 'Numéros') },
        { to: '/voice-tickets', icon: <PhoneCall />, label: t('sidebarNav.voiceCalls', 'Appels') },
      ],
    },
    {
      key: 'data',
      label: t('sidebarNav.dataTools', 'Données & outils'),
      icon: <Database />,
      items: [
        has(['admin', 'logistics'])
          && { to: '/connectors', icon: <Database />, label: t('sidebarNav.connectors', 'Sources de données') },
        // The one place a Shopify / PrestaShop shop is connected (orders,
        // parcel notes and tracking, catalogue). Configuration: not for
        // employees (the API answers them read-only, can_edit false).
        has(['admin', 'logistics'])
          && { to: '/shops', icon: <Store />, label: t('sidebarNav.shops', 'Boutiques') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/brands', icon: <Tag />, label: t('sidebarNav.brands', 'Marques') },
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/openapi-schemes', icon: <CodeXml />, label: t('sidebarNav.apiTools', 'API & outils') },
      ],
    },
    catalogueEnabled && {
      key: 'catalogue',
      label: t('catalogue.nav.title', 'Catalogue produits'),
      icon: <Store />,
      items: [
        { to: '/catalogue', icon: <Package />, label: t('catalogue.nav.products', 'Produits'), end: true },
        { to: '/catalogue?assistant=1', icon: <Bot />, label: t('catalogue.nav.assistant', 'Assistant marketing'), drawer: true },
        // Configuration (sources with their credentials, settings): admin
        // only — Kevin 29/10, an employee's settings are « Mon compte ».
        !isEmployee && { to: '/catalogue/connections', icon: <Plug />, label: t('catalogue.nav.connections', 'Connexions') },
        !isEmployee && { to: '/catalogue/settings', icon: <Settings />, label: t('catalogue.nav.settings', 'Réglages') },
      ],
    },
    has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience']) && {
      key: 'settings',
      label: t('parameters') || 'Paramètres',
      icon: <Settings />,
      items: [
        // Reachable here once finished or hidden; pinned on top while open.
        userRole === 'admin' && !onboarding
          && { to: '/onboarding', icon: <Flag />, label: t('sidebarNav.onboarding', 'Mise en route') },
        { to: '/parameters', icon: <Settings />, label: t('polishUi.nav.account', 'Compte') },
      ],
    },
    // Where the admin's Paramètres sit: the employee's own account (password).
    isEmployee && {
      key: 'settings',
      label: t('polishUi.nav.account', 'Compte'),
      icon: <UserRound />,
      items: [
        { to: '/parameters', icon: <UserRound />, label: t('accountScopeUi.nav.myAccount', 'Mon compte') },
      ],
    },
  ];

  // The section holding the current page opens by itself, so the active link
  // is always visible (a deep link — journey, notification — lands anywhere).
  const activeSectionKey = (sections.filter(Boolean).find((section) => section.items.filter(Boolean).some((item) => {
    const base = String(item.to || '').split('?')[0];
    return base && (location.pathname === base || location.pathname.startsWith(`${base}/`));
  })) || {}).key;
  useEffect(() => {
    if (activeSectionKey && !expandedSections[activeSectionKey]) {
      setExpandedSections((prev) => ({ ...prev, [activeSectionKey]: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionKey]);

  const handleBrandClick = () => {
    if (userRole === 'logistics') {
      navigate('/tickets/archive');
    } else {
      navigate('/dashboard');
    }
  };

  const roleLabels = {
    admin: t('sidebarNav.roleAdmin', 'Administrateur'),
    employee: t('sidebarNav.roleEmployee', 'Équipe'),
    logistics: t('sidebarNav.roleLogistics', 'Logistique'),
  };
  const collapseLabel = collapsed
    ? t('sidebarNav.expand', 'Déplier le menu')
    : t('sidebarNav.collapse', 'Replier le menu');

  const renderLink = (item, railTitle) => {
    const content = (
      <>
        <span className="sidebar-nav-icon">{item.icon}</span>
        <span className="sidebar-nav-label">{item.label}</span>
      </>
    );
    return item.drawer
      ? <Link to={item.to} title={railTitle}>{content}</Link>
      : <NavLink to={item.to} end={item.end} title={railTitle}>{content}</NavLink>;
  };

  const visibleSections = sections.filter(Boolean)
    .map((section) => ({ ...section, items: section.items.filter(Boolean) }))
    .filter((section) => section.items.length > 0);

  return (
    <aside ref={sidebarRef} className={`sidebar ${collapsed ? 'sidebar--rail' : ''}`}>
      <header className="sidebar-header">
        <button type="button" className="sidebar-brand" onClick={handleBrandClick} title="Unanimity">
          <BrandMark className="sidebar-brand-mark" />
          <span className="sidebar-brand-name">Unanimity</span>
        </button>
        <button
          type="button"
          className="sidebar-collapse"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapseLabel}
          title={collapseLabel}
        >
          {collapsed ? <ChevronRight aria-hidden="true" /> : <ChevronLeft aria-hidden="true" />}
        </button>
      </header>

      <nav className="sidebar-nav">
        {onboarding && (
          <NavLink to="/onboarding" className="sidebar-onboarding" title={t('sidebarNav.onboarding', 'Mise en route')}>
            <span className="sidebar-onboarding-top">
              <Flag aria-hidden="true" />
              <span className="sidebar-onboarding-label">{t('sidebarNav.onboarding', 'Mise en route')}</span>
              <span className="sidebar-onboarding-count">{onboarding.done}/{onboarding.total}</span>
            </span>
            <span className="sidebar-onboarding-meter" aria-hidden="true">
              <span style={{ width: `${onboarding.total ? Math.round((onboarding.done / onboarding.total) * 100) : 0}%` }} />
            </span>
          </NavLink>
        )}
        {collapsed ? (
          // Rail: every destination as an icon, one separator per group.
          visibleSections.map((section) => (
            <ul className="sidebar-rail-group" key={section.key} aria-label={section.label}>
              {section.items.map((item) => (
                <li key={item.to}>{renderLink(item, item.label)}</li>
              ))}
            </ul>
          ))
        ) : (
          visibleSections.map((section) => {
            const expanded = !!expandedSections[section.key];
            return (
              <div className={`sidebar-section ${section.key === activeSectionKey ? 'has-active' : ''}`} key={section.key}>
                <button
                  type="button"
                  className={`sidebar-section-header ${expanded ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.key)}
                  aria-expanded={expanded}
                >
                  <span className="sidebar-section-icon">{section.icon}</span>
                  <span className="sidebar-section-label">{section.label}</span>
                  <ChevronDown className="sidebar-section-chevron" aria-hidden="true" />
                </button>
                {expanded && (
                  <ul className="sidebar-menu">
                    {section.items.map((item) => (
                      <li key={item.to}>{renderLink(item)}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </nav>

      <footer className="sidebar-footer">
        <div className="sidebar-account" ref={accountMenuRef}>
          {/* Identity opens a quiet account menu — sign-out is a once-a-month
              action, not the most prominent button on the screen. */}
          <button
            type="button"
            className="sidebar-user-info"
            onClick={() => setAccountMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            title={userData?.email || ''}
          >
            <div className="sidebar-user-avatar">
              {userData?.email?.slice(0, 2).toUpperCase() || 'U'}
            </div>
            <div className="sidebar-user-details">
              <span className="sidebar-user-email">{userData?.email || ''}</span>
              <span className="sidebar-user-role">{roleLabels[userRole] || userRole || ''}</span>
            </div>
            <ChevronsUpDown className="sidebar-account-caret" aria-hidden="true" />
          </button>
          <NotificationBell />
          {accountMenuOpen && (
            <div className="sidebar-account-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => { setAccountMenuOpen(false); navigate('/parameters'); }}
              >
                {isEmployee ? <UserRound aria-hidden="true" /> : <Settings aria-hidden="true" />}
                {isEmployee
                  ? t('accountScopeUi.nav.myAccount', 'Mon compte')
                  : t('sidebarNav.userParameters', 'Compte & préférences')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={handleLogout}
              >
                <LogOut aria-hidden="true" />
                {t('logout', 'Déconnexion')}
              </button>
            </div>
          )}
        </div>
      </footer>
    </aside>
  );
};

export default Sidebar;
